import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Btn } from "@/pages/admin/managers/ui";
import {
  LayoutDashboard, Images, FolderTree, Briefcase, Package, CalendarCheck,
  MessageSquareQuote, HelpCircle, Settings, Mail, LogOut, Menu, X,
  CloudUpload, Loader2,
} from "lucide-react";
import PhotosManager from "@/pages/admin/managers/PhotosManager";
import CategoriesManager from "@/pages/admin/managers/CategoriesManager";
import ServicesManager from "@/pages/admin/managers/ServicesManager";
import PackagesManager from "@/pages/admin/managers/PackagesManager";
import BookingsManager from "@/pages/admin/managers/BookingsManager";
import TestimonialsManager from "@/pages/admin/managers/TestimonialsManager";
import FaqManager from "@/pages/admin/managers/FaqManager";
import MessagesManager from "@/pages/admin/managers/MessagesManager";
import SettingsManager from "@/pages/admin/managers/SettingsManager";

const NAV = [
  { key: "dashboard", label: "Inicio", icon: LayoutDashboard },
  { key: "photos", label: "Fotografías", icon: Images },
  { key: "categories", label: "Categorías", icon: FolderTree },
  { key: "services", label: "Servicios", icon: Briefcase },
  { key: "packages", label: "Paquetes", icon: Package },
  { key: "bookings", label: "Reservaciones", icon: CalendarCheck },
  { key: "testimonials", label: "Testimonios", icon: MessageSquareQuote },
  { key: "faqs", label: "Preguntas", icon: HelpCircle },
  { key: "messages", label: "Mensajes", icon: Mail },
  { key: "settings", label: "Configuración", icon: Settings },
];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("dashboard");
  const [sidebar, setSidebar] = useState(false);

  const doLogout = () => {
    logout();
    navigate("/admin");
  };

  return (
    <div className="admin-scope flex">
      {/* Sidebar */}
      <aside className={`fixed lg:static z-40 top-0 left-0 h-full w-64 bg-[#141416] border-r border-[#27272A] transform transition-transform lg:translate-x-0 flex flex-col ${sidebar ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="p-6 border-b border-[#27272A] shrink-0">
          <h2 className="font-serif text-2xl text-white leading-none">Yocaira Abreu</h2>
          <p className="overline-label mt-1">Administración</p>
        </div>
        <nav className="p-3 space-y-1 overflow-y-auto flex-1">
          {NAV.map((n) => (
            <button
              key={n.key}
              data-testid={`admin-nav-${n.key}`}
              onClick={() => { setTab(n.key); setSidebar(false); }}
              className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-md text-sm transition-colors ${
                tab === n.key ? "bg-[#C5A059] text-[#0A0A0A] font-semibold" : "text-[#A1A1AA] hover:bg-[#1f1f22] hover:text-white"
              }`}
            >
              <n.icon size={18} /> {n.label}
            </button>
          ))}
        </nav>
        <div className="p-3 border-t border-[#27272A] shrink-0">
          <button data-testid="admin-logout" onClick={doLogout} className="w-full flex items-center gap-3 px-4 py-2.5 rounded-md text-sm text-[#A1A1AA] hover:bg-[#1f1f22] hover:text-white transition-colors">
            <LogOut size={18} /> Cerrar sesión
          </button>
        </div>
      </aside>

      {sidebar && <div className="fixed inset-0 bg-black/50 z-30 lg:hidden" onClick={() => setSidebar(false)} />}

      {/* Main */}
      <main className="flex-1 min-w-0">
        <header className="sticky top-0 z-20 bg-[#0F0F10]/90 backdrop-blur border-b border-[#27272A] px-4 sm:px-8 py-4 flex items-center justify-between">
          <button className="lg:hidden text-white" onClick={() => setSidebar(true)}><Menu size={22} /></button>
          <h1 className="font-serif text-2xl text-white capitalize">{NAV.find((n) => n.key === tab)?.label}</h1>
          <span className="text-sm text-[#8C8375] hidden sm:block">{user?.email}</span>
        </header>
        <div className="p-4 sm:p-8">
          {tab === "dashboard" && <DashboardHome setTab={setTab} />}
          {tab === "photos" && <PhotosManager />}
          {tab === "categories" && <CategoriesManager />}
          {tab === "services" && <ServicesManager />}
          {tab === "packages" && <PackagesManager />}
          {tab === "bookings" && <BookingsManager />}
          {tab === "testimonials" && <TestimonialsManager />}
          {tab === "faqs" && <FaqManager />}
          {tab === "messages" && <MessagesManager />}
          {tab === "settings" && <SettingsManager />}
        </div>
      </main>
    </div>
  );
}

const DashboardHome = ({ setTab }) => {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/admin/stats").then(({ data }) => setStats(data)); }, []);
  const cards = [
    { label: "Fotografías", value: stats?.photos, tab: "photos", icon: Images },
    { label: "Categorías", value: stats?.categories, tab: "categories", icon: FolderTree },
    { label: "Reservas pendientes", value: stats?.bookings_pending, tab: "bookings", icon: CalendarCheck },
    { label: "Reservas totales", value: stats?.bookings_total, tab: "bookings", icon: CalendarCheck },
    { label: "Mensajes", value: stats?.messages, tab: "messages", icon: Mail },
  ];
  return (
    <div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {cards.map((c) => (
          <button key={c.label} onClick={() => setTab(c.tab)} className="text-left bg-[#18181B] border border-[#27272A] rounded-lg p-6 hover:border-[#C5A059] transition-colors">
            <c.icon size={22} className="text-[#C5A059]" />
            <p className="mt-4 text-3xl font-serif text-white">{c.value ?? "—"}</p>
            <p className="text-sm text-[#8C8375] mt-1">{c.label}</p>
          </button>
        ))}
      </div>
      <div className="mt-8 bg-[#18181B] border border-[#27272A] rounded-lg p-6">
        <h3 className="font-serif text-xl text-white">Bienvenida, Yocaira</h3>
        <p className="mt-2 text-sm text-[#A1A1AA]">Desde aquí puedes administrar todo tu sitio: sube fotografías, gestiona categorías, servicios, paquetes, reservaciones y más. Los cambios se guardan automáticamente y se reflejan en tu página al instante.</p>
      </div>
      <SyncCard />
    </div>
  );
};

const SyncCard = () => {
  const [st, setSt] = useState(null);

  const poll = async () => {
    try { const { data } = await api.get("/admin/sync-status"); setSt(data); return data; }
    catch { return null; }
  };

  useEffect(() => { poll(); }, []);

  useEffect(() => {
    if (!st?.running) return;
    const iv = setInterval(async () => {
      const d = await poll();
      if (d && !d.running) {
        clearInterval(iv);
        if (d.error) toast.error("Error al sincronizar: " + d.error);
        else toast.success("¡Sitio público sincronizado! Tus cambios ya están en vivo.");
      }
    }, 2500);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [st?.running]);

  const start = async () => {
    try {
      await api.post("/admin/sync-to-production");
      toast.info("Sincronización iniciada…");
      const d = await poll();
      setSt({ ...(d || {}), running: true });
    } catch (e) { toast.error(apiError(e)); }
  };

  if (!st || !st.available) return null;
  const pct = st.total ? Math.round((st.done / st.total) * 100) : 0;

  return (
    <div data-testid="sync-card" className="mt-8 bg-[#18181B] border border-[#C5A059]/40 rounded-lg p-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <h3 className="font-serif text-xl text-white flex items-center gap-2">
            <CloudUpload size={20} className="text-[#C5A059]" /> Publicar cambios en el sitio público
          </h3>
          <p className="mt-2 text-sm text-[#A1A1AA] max-w-xl">
            Copia tus fotos y cambios de la vista previa a tu dominio <strong className="text-[#E4E4E7]">yocairaphotography.com</strong>. Solo se suben las fotos nuevas o modificadas.
          </p>
          {st.last_sync && !st.running && (
            <p className="mt-2 text-xs text-[#8C8375]" data-testid="sync-last">
              Última sincronización: {new Date(st.last_sync).toLocaleString("es")}
            </p>
          )}
          {st.error && !st.running && (
            <p className="mt-2 text-xs text-red-400">Último error: {st.error}</p>
          )}
        </div>
        <Btn data-testid="sync-to-prod-btn" onClick={start} disabled={st.running}>
          {st.running
            ? <><Loader2 size={16} className="inline mr-1 animate-spin" /> Sincronizando…</>
            : <><CloudUpload size={16} className="inline mr-1" /> Sincronizar ahora</>}
        </Btn>
      </div>
      {st.running && (
        <div className="mt-4" data-testid="sync-progress">
          <div className="flex justify-between text-xs text-[#8C8375] mb-1">
            <span>{st.message || "Trabajando…"}</span>
            <span>{st.done}/{st.total}</span>
          </div>
          <div className="h-2 rounded-full bg-[#27272A] overflow-hidden">
            <div className="h-full bg-[#C5A059] transition-all" style={{ width: `${pct}%` }} />
          </div>
        </div>
      )}
    </div>
  );
};
